import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, UserCheck, SearchIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { getErrorMessage, getErrorStatus } from '@/shared/services/api';
import { Spinner } from '@/shared/components/Spinner';
import { cn } from '@/shared/utils/cn';
import { formatUserLabel } from '@/shared/utils/user';
import {
  FEED_ITEM_ASSIGNMENT_NOTES_MAX,
  FeedItemAssignmentDto,
  UserLookupDto,
} from '@/shared/types/api';
import { userLookupApi } from '@/shared/services/userLookupApi';
import { feedItemAssignmentApi } from '../services/feedItemAssignmentApi';
import { isClearAssignment, validateAssignment } from '../lib/assignmentValidation';

interface AssignToUserModalProps {
  feedItemId: number;
  /**
   * The item's assignment as the feed row has it; null when unassigned. Only a fallback: the
   * modal re-reads the assignment when it opens, because the row may be out of date.
   */
  currentAssignment: FeedItemAssignmentDto | null;
  onClose: () => void;
  /**
   * Called with the item's latest assignment (null when it has none): the saved one, or the one
   * reloaded after the save was refused because someone else changed it first.
   */
  onSaved: (assignment: FeedItemAssignmentDto | null) => void;
}

interface UserOption {
  id: string;
  fullName: string;
  email?: string;
  isActive: boolean;
}

/**
 * SC-423: assign a feed item to one or more active users with a Review by Date and notes.
 * Pre-filled with the item's assignment as freshly read from the server, and saved against that
 * read: if someone else changed it meanwhile, the API refuses with 409 and the modal reloads
 * rather than overwriting their work. Cancel (or Escape) discards changes. Clearing users,
 * date and notes and pressing Update removes the assignment. The field rules are the server's
 * (see assignmentValidation.ts), so a form this accepts is one the API accepts.
 */
export function AssignToUserModal({ feedItemId, currentAssignment, onClose, onSaved }: AssignToUserModalProps) {
  const [activeUsers, setActiveUsers] = useState<UserLookupDto[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  // The assignment the form was filled from, and that a save is checked against.
  const [base, setBase] = useState<FeedItemAssignmentDto | null>(currentAssignment);
  const [assignmentLoading, setAssignmentLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    currentAssignment?.assignees.map((a) => a.userId) ?? []
  );
  const [reviewByDate, setReviewByDate] = useState(currentAssignment?.reviewByDate ?? '');
  const [notes, setNotes] = useState(currentAssignment?.notes ?? '');
  const [userFilter, setUserFilter] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saving, setSaving] = useState(false);

  const fillForm = useCallback((assignment: FeedItemAssignmentDto | null) => {
    setBase(assignment);
    setSelectedIds(assignment?.assignees.map((a) => a.userId) ?? []);
    setReviewByDate(assignment?.reviewByDate ?? '');
    setNotes(assignment?.notes ?? '');
    setSubmitAttempted(false);
  }, []);

  // The feed row was loaded earlier and may predate someone else's change, so start from the
  // server's current assignment. If that read fails, the row's copy is still checked on save.
  useEffect(() => {
    let cancelled = false;
    feedItemAssignmentApi
      .get(feedItemId)
      .then((fresh) => {
        if (!cancelled) fillForm(fresh ?? null);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getErrorMessage(error, 'Failed to load the current assignment'));
      })
      .finally(() => {
        if (!cancelled) setAssignmentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feedItemId, fillForm]);

  useEffect(() => {
    let cancelled = false;
    userLookupApi
      .list(true)
      .then((users) => {
        if (!cancelled) setActiveUsers(users);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getErrorMessage(error, 'Failed to load users'));
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Only active users can be picked, but someone deactivated after being assigned stays on the
  // item until removed (SC-423 open question 2), so they are listed too, marked inactive.
  const options = useMemo<UserOption[]>(() => {
    const byId = new Map<string, UserOption>(activeUsers.map((u) => [u.id, u]));
    for (const assignee of base?.assignees ?? []) {
      if (!byId.has(assignee.userId)) {
        byId.set(assignee.userId, { id: assignee.userId, fullName: assignee.fullName, isActive: false });
      }
    }
    return [...byId.values()].sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [activeUsers, base]);

  const visibleOptions = useMemo(() => {
    const needle = userFilter.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) =>
        selectedIds.includes(o.id) ||
        o.fullName.toLowerCase().includes(needle) ||
        (o.email ?? '').toLowerCase().includes(needle)
    );
  }, [options, userFilter, selectedIds]);

  const values = { userIds: selectedIds, reviewByDate, notes };
  const error = validateAssignment(values);
  const showError = submitAttempted && error !== null;
  const isUpdate = base !== null;

  const toggleUser = (id: string) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  // Someone else changed the assignment after this modal read it: show theirs (and put it on the
  // row) so the user decides again from the current state instead of overwriting it blind.
  const reloadAfterConflict = async () => {
    try {
      const latest = (await feedItemAssignmentApi.get(feedItemId)) ?? null;
      fillForm(latest);
      onSaved(latest);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load the current assignment'));
    }
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (error) return;

    const clearing = isClearAssignment(values);
    try {
      setSaving(true);
      const saved = await feedItemAssignmentApi.set(feedItemId, {
        userIds: selectedIds,
        reviewByDate: reviewByDate || null,
        notes: notes.trim() ? notes.trim() : null,
        checkUnchanged: true,
        expectedUpdatedAt: base?.updatedAt ?? null,
      });
      toast.success(clearing ? 'Assignment removed' : 'Feed item assigned');
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save the assignment'));
      if (getErrorStatus(err) === 409) await reloadAfterConflict();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-lg animate-slide-up p-6 shadow-modal max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-user-title"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 id="assign-user-title" className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <UserCheck className="h-5 w-5 text-primary" />
            Assign to User
          </h2>
          <button type="button" onClick={onClose} className="btn-icon btn-ghost btn-sm" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <fieldset>
            <legend className="label">Select User(s)</legend>
            {usersLoading || assignmentLoading ? (
              <div className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> {usersLoading ? 'Loading users…' : 'Loading assignment…'}
              </div>
            ) : (
              <>
                {options.length > 6 && (
                  <div className="relative mt-1.5">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      placeholder="Filter users…"
                      aria-label="Filter users"
                      className="input pl-10"
                    />
                  </div>
                )}
                <ul
                  className={cn(
                    'mt-1.5 max-h-48 space-y-0.5 overflow-y-auto rounded-md border p-1',
                    showError && error?.field === 'users' ? 'border-destructive' : 'border-border'
                  )}
                >
                  {visibleOptions.length === 0 && (
                    <li className="px-2 py-1.5 text-sm text-muted-foreground">
                      {userFilter.trim() ? `No users match "${userFilter.trim()}"` : 'No users available'}
                    </li>
                  )}
                  {visibleOptions.map((option) => (
                    <li key={option.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(option.id)}
                          onChange={() => toggleUser(option.id)}
                        />
                        <span className={cn('text-foreground', !option.isActive && 'text-muted-foreground')}>
                          {formatUserLabel(option)}
                        </span>
                        {option.email && (
                          <span className="ml-auto truncate text-xs text-muted-foreground">{option.email}</span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </fieldset>

          <div>
            <label htmlFor="assign-review-by" className="label">
              Review by Date
            </label>
            <input
              id="assign-review-by"
              type="date"
              value={reviewByDate}
              onChange={(e) => setReviewByDate(e.target.value)}
              disabled={assignmentLoading}
              className={cn('input mt-1.5', showError && error?.field === 'reviewByDate' && 'border-destructive')}
            />
          </div>

          <div>
            <label htmlFor="assign-notes" className="label">
              Notes
            </label>
            <textarea
              id="assign-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={assignmentLoading}
              rows={3}
              maxLength={FEED_ITEM_ASSIGNMENT_NOTES_MAX}
              className={cn('input mt-1.5 resize-none', showError && error?.field === 'notes' && 'border-destructive')}
              placeholder="Optional notes for the assignee(s)…"
            />
            <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground">
              {notes.length}/{FEED_ITEM_ASSIGNMENT_NOTES_MAX}
            </p>
          </div>

          {showError && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error.message}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || usersLoading || assignmentLoading}
              className="btn-primary flex-1"
            >
              {saving && <Spinner className="text-primary-foreground" />}
              {saving ? 'Saving…' : isUpdate ? 'Update' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
